import {useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {socket} from '@/socket'
import {ExportJob} from '@/features/exports/types'
import {patchExportInCache} from '@/features/exports/query/exportsCache'
import {useExportsIndicatorStore} from '@/features/exports/hooks/useExportsIndicator'
import {useExportAnnouncementStore} from '@/features/exports/hooks/useExportAnnouncement'

/**
 * Subscribes to the `export:status_changed` event on the shared socket and reconciles the
 * React Query cache live (FR-012/FR-013). The `users:{userId}` room is already joined by
 * `useSocketNotifications`, so this hook only adds/removes the listener.
 *
 * On `ready`/`failed` it raises a popover announcement anchored to the exports toggle (ready offers
 * a direct download action, failed shows the reason), replacing any pending announcement for that
 * export. Every status change also drives the toggle dot indicator regardless of whether the
 * sidebar is open (FR-014/FR-015).
 */
export const useExportsSocket = (userId: string) => {
    const queryClient = useQueryClient()

    useEffect(() => {
        if (!userId) return

        const handler = (raw: unknown) => {
            const parsed = ExportJob.safeParse(raw)
            if (!parsed.success) {
                console.error('Invalid export:status_changed payload received:', parsed.error)
                return
            }
            const job = parsed.data
            patchExportInCache(queryClient, job)
            // Update the toggle dot for every status change (pending/processing → orange,
            // ready → green, failed → red); terminal non-dot states clear the job.
            useExportsIndicatorStore.getState().track(job.id, job.status)

            if (job.status === 'ready' || job.status === 'failed') {
                useExportAnnouncementStore.getState().announce(job)
            }
        }

        socket.on('export:status_changed', handler)

        return () => {
            socket.off('export:status_changed', handler)
        }
    }, [userId, queryClient])
}
